import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { MapPin, Star } from "lucide-react";
import { Link } from "react-router-dom";
import CoachAvatar from "@/components/CoachAvatar";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useI18n } from "@/lib/i18n";
import ShareButton from "@/components/ShareButton";

export default function CoachesPage() {
  const [coaches, setCoaches] = useState([]);
  const { t } = useI18n();
  const [discipline, setDiscipline] = useState("");
  const [city, setCity] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);
  useEffect(() => { api.get("/coaches").then((r) => setCoaches(r.data)); }, []);
  const todayIdx = (new Date().getDay() + 6) % 7;
  const disciplines = [...new Set(coaches.flatMap((c) => c.specialties || []))].sort();
  const cities = [...new Set(coaches.map((c) => c.city).filter(Boolean))].sort();
  const availableToday = (c) => c.available !== false && (c.availability || []).some((a) => a.day === todayIdx);
  const filtered = coaches.filter((c) => (!discipline || (c.specialties || []).includes(discipline)) && (!city || c.city === city) && (!todayOnly || availableToday(c)));
  const reset = () => { setDiscipline(""); setCity(""); setTodayOnly(false); };
  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10 sc-fade-in">
        <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{t("Communauté")}</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{t("Coachs disponibles")}</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">{t("Découvrez les coachs adhérents à la plateforme, prêts à réserver une salle près de chez vous.")}</p>
        <div className="mt-6 flex flex-wrap gap-2 items-center" data-testid="coach-filters">
          <select data-testid="coach-filter-discipline" value={discipline} onChange={(e) => setDiscipline(e.target.value)} className="px-3 py-2 rounded-full border border-slate-200 bg-white text-sm"><option value="">{t("Toutes disciplines")}</option>{disciplines.map((d) => <option key={d} value={d}>{d}</option>)}</select>
          <select data-testid="coach-filter-city" value={city} onChange={(e) => setCity(e.target.value)} className="px-3 py-2 rounded-full border border-slate-200 bg-white text-sm"><option value="">{t("Toutes villes")}</option>{cities.map((d) => <option key={d} value={d}>{d}</option>)}</select>
          <label data-testid="coach-filter-today" className={`flex items-center gap-2 px-3 py-2 rounded-full border text-sm cursor-pointer ${todayOnly ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700" : "bg-white border-slate-200 text-slate-600"}`}><input type="checkbox" className="accent-emerald-500" checked={todayOnly} onChange={(e) => setTodayOnly(e.target.checked)} /> {t("Disponible aujourd'hui")}</label>
          {(discipline || city || todayOnly) && <button data-testid="coach-filter-reset" onClick={reset} className="text-xs text-slate-500 underline">{t("Réinitialiser")}</button>}
          <span className="text-sm text-slate-500 ml-auto" data-testid="coach-count">{filtered.length} coach(s)</span>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.length === 0 && <div className="text-slate-500 text-sm">Aucun coach ne correspond à ces filtres.</div>}
          {filtered.map((c) => (
            <div key={c.id} data-testid={`coach-card-${c.id}`} className="sc-card">
              {availableToday(c) && <div data-testid={`coach-today-${c.id}`} className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1">● Disponible aujourd'hui</div>}
              <div className="flex gap-3 items-start">
                <CoachAvatar coach={c} />
                <div>
                  <Link to={`/coachs/${c.id}`} data-testid={`coach-link-${c.id}`} className="font-bold text-slate-900 hover:underline">{c.name}</Link>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{c.city || "France"}
                    {c.rating_avg != null && <span className="ml-1 flex items-center gap-0.5 text-orange-600"><Star className="w-3 h-3" />{c.rating_avg}</span>}
                  </div>
                  <div className="mt-1"><VerifiedBadge coach={c} /></div>
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-700 line-clamp-3">{c.bio || "Coach adhérent TymCoach."}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(c.specialties || []).map((s) => <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-700 border border-orange-500/20">{s}</span>)}
              </div>
              <div className="mt-3 flex justify-between items-center">
                <Link to={`/coachs/${c.id}`} data-testid={`coach-profile-btn-${c.id}`} className="sc-btn sc-btn-outline text-xs">Voir le profil</Link>
                <ShareButton compact testId={`share-coach-${c.id}`} path={`/coachs/${c.id}`} text={`Découvre ${c.name}, coach ${(c.specialties || []).slice(0, 2).join(" & ")} sur TymCoach :`} />
              </div>
            </div>
          ))}
          {coaches.length === 0 && <div className="text-slate-500 text-sm">Aucun coach pour le moment.</div>}
        </div>
      </div>
      <Footer />
    </div>
  );
}
